import type { JobDetail } from './job-by-id-query';

export type JobSectionId =
  | 'general-info'
  | 'flow'
  | 'jobs'
  | 'work-conditions'
  | 'compensation'
  | 'education'
  | 'requirements'
  | 'faqs';

export interface JobSection {
  id: JobSectionId;
  title: string;
  description: string;
  optional?: boolean;
}

const FILLED_DESCRIPTION = 'Preenchido';

const PLACEHOLDER = 'Seção ainda não preenchida';

const PLACEHOLDERS: Record<JobSectionId, string> = {
  'general-info': PLACEHOLDER,
  flow: PLACEHOLDER,
  jobs: PLACEHOLDER,
  'work-conditions': PLACEHOLDER,
  compensation: PLACEHOLDER,
  education: PLACEHOLDER,
  requirements: PLACEHOLDER,
  faqs: PLACEHOLDER,
};

/**
 * EU-709 RN-20 / BDT-027 — a lista de seções opcionais vem da API.
 *
 * Aqui vivia um `Set` fixo (`jobs`, `requirements`, `faqs`). Com a chegada do
 * tipo Banco de talentos essa lista passou a depender do TIPO da vaga, e a
 * RN-20 é explícita: "a obrigatoriedade dos campos é resolvida no backend a
 * partir do tipo selecionado. O frontend apenas reflete o que a API informa —
 * é proibido hardcodear no cliente a lista de campos ou de seções que se
 * tornam opcionais".
 *
 * Sem contrato (vaga carregando, ou janela de deploy em que o BFF ainda não o
 * emite) NENHUMA tag é exibida. Um palpite local aqui poderia discordar do
 * gate de publicação, que é justamente o modo de falha que esta entrega veio
 * consertar: o consultor ver "opcional" na tela e levar bloqueio ao publicar.
 *
 * `general-info` e `work-conditions` nunca recebem a tag (RN-09, RN-10) — a
 * guarda é redundante com o backend e existe para a tela não depender disso.
 */
const NEVER_OPTIONAL_IDS = new Set<JobSectionId>(['general-info', 'work-conditions']);

function optionalSectionIds(job: JobDetail | undefined): Set<JobSectionId> {
  const fromApi = job?.requirements?.optionalSections;
  if (!fromApi) return new Set<JobSectionId>();
  return new Set((fromApi as JobSectionId[]).filter((id) => !NEVER_OPTIONAL_IDS.has(id)));
}

const TITLES: Record<JobSectionId, string> = {
  'general-info': 'Informações gerais',
  flow: 'Etapas de seleção',
  jobs: 'Vagas',
  'work-conditions': 'Condições de trabalho',
  compensation: 'Remuneração',
  education: 'Formação',
  requirements: 'Requisitos e diferenciais',
  faqs: 'Perguntas frequentes',
};

const SECTION_ORDER: JobSectionId[] = [
  'general-info',
  'flow',
  'jobs',
  'work-conditions',
  'compensation',
  'education',
  'requirements',
  'faqs',
];

function isGeneralInfoFilled(job: JobDetail): boolean {
  // Por AC EU-158 RN-02 + FR-041: a seção é considerada preenchida quando os
  // dois obrigatórios (Nome e Programa) estão setados. Descrição e Meta de
  // inscritos são opcionais e NÃO MUST pesar nesta flag — senão vagas
  // perfeitamente válidas aparecem como "ainda não preenchida" no hub.
  return Boolean(job.name?.trim()) && Boolean(job.programId);
}

function isWorkConditionsFilled(job: JobDetail): boolean {
  // O tipo de contratação migrou para o PROGRAMA (não é mais autorado aqui), então
  // NÃO entra mais nesta flag — senão a seção fica eternamente "ainda não
  // preenchida" mesmo com modelo/turno/carga salvos.
  return (
    Boolean(job.workModel) &&
    Boolean(job.shift) &&
    job.weeklyHours !== null &&
    job.weeklyHours !== undefined
  );
}

function isCompensationFilled(job: JobDetail): boolean {
  return Boolean(job.salaryDisplay);
}

function isEducationFilled(job: JobDetail): boolean {
  // EU-162 follow-up: ambas as datas (min + max) são opcionais agora; o que
  // sinaliza "preenchido" é o consultor ter selecionado ao menos um tipo de
  // formação. As datas são metadados opcionais para refinar o filtro.
  return (job.graduationTypes?.length ?? 0) > 0;
}

function isRequirementsFilled(job: JobDetail): boolean {
  return (
    (job.competencias?.length ?? 0) > 0 ||
    (job.diferenciais?.length ?? 0) > 0 ||
    (job.diversityCriteria?.length ?? 0) > 0 ||
    (job.disabilityDetails?.selectedIds?.length ?? 0) > 0 ||
    job.ageMin !== null ||
    job.ageMax !== null
  );
}

function isFaqsFilled(job: JobDetail): boolean {
  return (job.faqs?.length ?? 0) > 0;
}

function isFlowFilled(job: JobDetail): boolean {
  return (job.phases?.length ?? 0) > 0;
}

function isJobsFilled(job: JobDetail): boolean {
  return (job.jobs?.length ?? 0) > 0;
}

function describe(id: JobSectionId, job: JobDetail | undefined): string {
  if (!job) return PLACEHOLDERS[id];
  switch (id) {
    case 'general-info':
      return isGeneralInfoFilled(job) ? FILLED_DESCRIPTION : PLACEHOLDERS[id];
    case 'work-conditions':
      return isWorkConditionsFilled(job) ? FILLED_DESCRIPTION : PLACEHOLDERS[id];
    case 'compensation':
      return isCompensationFilled(job) ? FILLED_DESCRIPTION : PLACEHOLDERS[id];
    case 'education':
      return isEducationFilled(job) ? FILLED_DESCRIPTION : PLACEHOLDERS[id];
    case 'requirements':
      return isRequirementsFilled(job) ? FILLED_DESCRIPTION : PLACEHOLDERS[id];
    case 'faqs': {
      const count = job.faqs?.length ?? 0;
      if (!isFaqsFilled(job)) return PLACEHOLDERS[id];
      return count === 1 ? '1 pergunta' : `${count} perguntas`;
    }
    case 'flow': {
      if (!isFlowFilled(job)) return PLACEHOLDERS[id];
      const phaseCount = job.phases?.length ?? 0;
      const stageCount = (job.phases ?? []).reduce(
        (acc, phase) => acc + (phase.stages?.length ?? 0),
        0
      );
      const phaseLabel = phaseCount === 1 ? '1 fase' : `${phaseCount} fases`;
      const stageLabel = stageCount === 1 ? '1 etapa' : `${stageCount} etapas`;
      return `${phaseLabel} · ${stageLabel}`;
    }
    case 'jobs': {
      if (!isJobsFilled(job)) return PLACEHOLDERS[id];
      const count = job.jobs?.length ?? 0;
      return count === 1 ? '1 vaga' : `${count} vagas`;
    }
  }
}

export function buildJobSections(job: JobDetail | undefined): JobSection[] {
  const optionalIds = optionalSectionIds(job);
  return SECTION_ORDER.map((id) => ({
    id,
    title: TITLES[id],
    description: describe(id, job),
    optional: optionalIds.has(id) ? true : undefined,
  }));
}
